import { gettext } from '@eventbrite/i18n';
import sortBy from 'lodash/sortBy';
import React, { ComponentProps, FunctionComponent } from 'react';
import { CAPACITY_WAITLIST } from '../../constants';
import { EventLogoFocalPoint } from '../../containers/pane/connectPane';
import { CurrencyBasedValue } from '../../models/currency';
import { Ticket } from '../../models/tickets';
import { formatTicketsForOrderSummary } from '../../utils/tickets';
import { getTicketsAvailableOnSale } from '../ticketSelection/utils/ticketUtils';
import { EmptyPane } from './EmptyPane';
import { ItemizedPaneContent } from './ItemizedPaneContent';
import { getSummaryItems, getTotalCostInfo } from './utils';

export const PaneContent: FunctionComponent<
    ComponentProps<typeof ItemizedPaneContent> & {
        isAddAttendeesOrder: boolean;
        isAppRebrandActive: boolean;
        isEventWithShippingFee: boolean;
        isSeriesInfiniteCalendar: boolean;
        isPaymentView: boolean;
        isSeriesParentPage: boolean;
        isTransferredOrder: boolean;
        isWaitlist: boolean;
        orderCosts?: {
            hasGtsTax: boolean;
            displayTax?: {
                name: string;
                tax: CurrencyBasedValue;
            };
        };
        selectedTickets: Array<Ticket>;
        ticketsById: Record<string, Ticket>;
        waitlistTicketId: string;
        eventLogoFocalPoint: EventLogoFocalPoint;
    }
> = ({
    breakdownItems,
    currency,
    currencyFormat,
    eventDate,
    eventImageSrc,
    installments,
    isAddAttendeesOrder,
    isAppRebrandActive,
    isEventWithShippingFee,
    isSeriesInfiniteCalendar,
    isPaymentView,
    isSeriesParentPage,
    isTransferredOrder,
    isWaitlist,
    onPaneCloseButtonClick,
    orderCosts,
    paneInteraction,
    selectedTickets,
    selectedVirtualIncentives,
    ticketsById,
    totalCost,
    unformattedOrderCost,
    waitlistTicketId,
}) => {
    // TODO: this is a temporary fix to avoid displaying order summary when it's an old
    // shipping event due to discrepancies in the values. It should be cleaned up after
    // meeting with pricing team and getting to know more about types of taxes.
    if (isEventWithShippingFee && !isPaymentView) {
        return <EmptyPane isSeriesParentPage={isSeriesParentPage} />;
    }

    const showEventDate =
        !isSeriesInfiniteCalendar && isPaymentView ? eventDate : null;

    if (isWaitlist) {
        const name =
            waitlistTicketId !== CAPACITY_WAITLIST &&
            ticketsById[waitlistTicketId]
                ? gettext('Waitlist Entry for %(ticketName)s', {
                      ticketName: ticketsById[waitlistTicketId].name,
                  })
                : gettext('Waitlist Entry');

        const items = getSummaryItems({
            selectedTickets: [
                {
                    id: waitlistTicketId,
                    name: name.toString(),
                    selectedQuantity: 1,
                },
            ],
            eventCurrency: currency,
            currencyFormat,
            shouldHandleFreeTickets: false,
        });

        return (
            <ItemizedPaneContent
                eventDate={showEventDate}
                eventImageSrc={eventImageSrc}
                isAppRebrandActive={isAppRebrandActive}
                isSeriesInfiniteCalendar={isSeriesInfiniteCalendar}
                items={items}
                totalCost={totalCost}
                installments={installments}
                currency={currency}
                currencyFormat={currencyFormat}
                selectedVirtualIncentives={selectedVirtualIncentives}
                unformattedOrderCost={unformattedOrderCost}
                paneInteraction={paneInteraction}
                onPaneCloseButtonClick={onPaneCloseButtonClick}
                isPaymentView={isPaymentView}
            />
        );
    }

    // If we have an existing order, we consider it has been created previously
    // by an administrator or is a transfer, and therefore will be able to access
    // all tickets, not only the on sale ones
    const selectedTicketsOnSale =
        isAddAttendeesOrder || isTransferredOrder
            ? selectedTickets
            : getTicketsAvailableOnSale(selectedTickets);

    if (!selectedTicketsOnSale.length) {
        return <EmptyPane isSeriesParentPage={isSeriesParentPage} />;
    }

    // UX decision to sort tickets with admission first and then addons
    const selectedTicketsWithAdmissionFirst = sortBy(
        formatTicketsForOrderSummary(selectedTicketsOnSale),
        [(ticket) => ticket.category === 'add_on'],
    );

    const totalCostInfo = orderCosts
        ? orderCosts.hasGtsTax && !orderCosts.displayTax
            ? gettext('Incl. sales tax')
            : null
        : getTotalCostInfo(selectedTickets);

    const items = getSummaryItems({
        selectedTickets: selectedTicketsWithAdmissionFirst,
        eventCurrency: currency,
        currencyFormat,
        shouldHandleFreeTickets: isAddAttendeesOrder,
    });

    return (
        <ItemizedPaneContent
            eventDate={showEventDate}
            eventImageSrc={eventImageSrc}
            totalCostInfo={totalCostInfo}
            isAppRebrandActive={isAppRebrandActive}
            isSeriesInfiniteCalendar={isSeriesInfiniteCalendar}
            items={items}
            breakdownItems={breakdownItems}
            totalCost={totalCost}
            installments={installments}
            currency={currency}
            currencyFormat={currencyFormat}
            selectedVirtualIncentives={selectedVirtualIncentives}
            unformattedOrderCost={unformattedOrderCost}
            paneInteraction={paneInteraction}
            onPaneCloseButtonClick={onPaneCloseButtonClick}
            isPaymentView={isPaymentView}
        />
    );
};
